import { createHandlerChainRunner } from "@insite/client-framework/HandlerCreator";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { MGBW_DEFAULT_SELECTIONS, MGBW_IS_CONFIGURABLE, MGBW_PRODUCT_OPTIONS } from "@tSurya/Common/MgbwPropertyKeys";
import applyItemAttributeOverrides from "@tSurya/Handlers/Configurator/ApplyItemAttributeOverrides";
import { getSelectionsFromUrl, updateUrlWithSelections } from "@tSurya/Handlers/Configurator/ConfiguratorUrlUtils";
import { getConfiguratorItemAttributes, postConfigurationData } from "@tSurya/Services/ConfiguratorService";
import { ConfigurationDataResultModel, ConfiguratorProductModel } from "@tSurya/Store/Data/ConfiguratorModels";
import { findMatchingStyleId } from "@tSurya/Store/Data/ConfiguratorSelectors";
import { CustomHandler } from "@tSurya/Store/CustomHandlerCreator/CustomHandlerCreator";

interface InitializeConfiguratorParameter {
    product: ProductModel;
}

type Props = {
    productConfig: ConfiguratorProductModel;
    initialSelections: Record<string, string>;
    configurationResult: ConfigurationDataResultModel;
    hasUrlSelections: boolean;
};

type HandlerType = CustomHandler<InitializeConfiguratorParameter, Props>;

export const CheckIsConfigurable: HandlerType = props => {
    const { product } = props.parameter;
    if (product.properties[MGBW_IS_CONFIGURABLE] !== "true") {
        return false;
    }
};

export const ParseProductConfig: HandlerType = props => {
    const { product } = props.parameter;

    var rawData = product.properties[MGBW_PRODUCT_OPTIONS];
    if (!rawData) {
        return false;
    }

    try {
        props.productConfig = JSON.parse(rawData);
    } catch {
        return false;
    }

    // Dispatch Initialize with productConfig immediately so the UI can render
    // the option shells while we resolve selections
    props.dispatch({
        type: "Custom/Configurator/Initialize",
        productConfig: props.productConfig,
    });
};

export const ReadUrlSelections: HandlerType = props => {
    var knownOptionIds = new Set(props.productConfig.options.map(o => o.optionId));
    var urlSelections = getSelectionsFromUrl(knownOptionIds);

    if (Object.keys(urlSelections).length > 0) {
        props.initialSelections = urlSelections;
        props.hasUrlSelections = true;
        return;
    }

    props.hasUrlSelections = false;
    props.initialSelections = {};
};

export const FetchItemAttributes: HandlerType = async props => {
    // One fetch per product; overrides are resolved locally by resolvedItemId.
    try {
        var itemAttributes = await getConfiguratorItemAttributes(props.productConfig.productId);
        props.dispatch({
            type: "Custom/Configurator/SetItemAttributes",
            itemAttributes,
        });
    } catch {
        // Non-fatal: PDP still renders with baseline product values.
        props.dispatch({
            type: "Custom/Configurator/SetItemAttributes",
            itemAttributes: [],
        });
    }
};

export const ResolveInitialSelections: HandlerType = async props => {
    var { product } = props.parameter;

    // Try pre-computed defaults from the GET product response first.
    // When the backend receives mgbwSelections via the product API query string,
    // mgbw_defaultSelections already reflects the URL selections, so no POST
    // is needed. This is critical for SSR to render correct configurator state.
    var rawDefaults = product.properties[MGBW_DEFAULT_SELECTIONS];
    if (rawDefaults) {
        try {
            props.configurationResult = JSON.parse(rawDefaults);
            return;
        } catch {
            // Fall through to POST
        }
    }

    // POST to validate selections (URL-based or empty) when defaults are
    // not available (e.g. product loaded without mgbwSelections param).
    props.dispatch({ type: "Custom/Configurator/BeginUpdate" });
    try {
        props.configurationResult = await postConfigurationData({
            productId: props.productConfig.productId,
            selections: props.hasUrlSelections ? props.initialSelections : {},
        });
    } catch (e) {
        var errorMessage = e instanceof Error ? e.message : "Failed to initialize configuration";
        props.dispatch({
            type: "Custom/Configurator/FailUpdate",
            error: errorMessage,
        });
        return false;
    }
};

export const DispatchInitialComplete: HandlerType = props => {
    props.dispatch({
        type: "Custom/Configurator/CompleteUpdate",
        selections: props.configurationResult.selections,
        configurationResult: props.configurationResult,
    });
};

export const DetectInitialStyle: HandlerType = props => {
    var styles = props.productConfig.preConfiguredStyles;
    if (!styles || styles.length === 0) {
        return;
    }

    var matchedStyleId: string | null = null;

    if (props.hasUrlSelections) {
        // URL selections present: detect if they match a known style
        matchedStyleId = findMatchingStyleId(styles, props.configurationResult.selections);
    } else {
        // No URL selections: default style is selected
        var defaultStyle = styles.find(s => s.isDefault) || styles[0];
        matchedStyleId = defaultStyle?.styleId ?? null;
    }

    props.dispatch({
        type: "Custom/Configurator/SetSelectedStyleId",
        selectedStyleId: matchedStyleId,
    });
};

export const ApplyOverrides: HandlerType = props => {
    props.dispatch(applyItemAttributeOverrides({}));
};

export const SyncUrlOnInit: HandlerType = props => {
    var knownOptionIds = new Set(props.productConfig.options.map(o => o.optionId));
    updateUrlWithSelections(props.configurationResult.selections, knownOptionIds);
};

export const chain = [
    CheckIsConfigurable,
    ParseProductConfig,
    ReadUrlSelections,
    FetchItemAttributes,
    ResolveInitialSelections,
    DispatchInitialComplete,
    DetectInitialStyle,
    ApplyOverrides,
    SyncUrlOnInit,
];

const initializeConfigurator = createHandlerChainRunner(chain, "InitializeConfigurator");
export default initializeConfigurator;
